import { z } from "zod"
import { convertHHmmToDate } from "./utils"

const ManualHourReportFormSchema = z.object({
  date: z.string(),
  projectId: z.string().nullable().optional(),
  taskId: z.string().nullable().optional(),
  method: z.literal("manual"),
  hours: z.number().min(0),
  minutes: z.number().min(0).optional(),
  description: z.string().optional(),
  membershipId: z.string().nullable(),
})

const IntervalHourReportFormSchema = z.object({
  date: z.string(),
  projectId: z.string().nullable().optional(),
  taskId: z.string().nullable().optional(),
  method: z.literal("interval"),
  startTime: z.string(),
  endTime: z.string(),
  description: z.string().optional(),
  membershipId: z.string().nullable(),
})

const TimerHourReportFormSchema = z.object({
  date: z.string(),
  projectId: z.string().nullable().optional(),
  taskId: z.string().nullable().optional(),
  method: z.literal("timer"),
  timerStartTime: z.string().optional(),
  timerEndTime: z.string().optional(),
  timerElapsedHours: z.number().optional(),
  description: z.string().optional(),
  membershipId: z.string().nullable(),
})

const HourReportFormSchema = z.discriminatedUnion("method", [
  ManualHourReportFormSchema,
  IntervalHourReportFormSchema,
  TimerHourReportFormSchema,
])

const ensureStartBeforeEnd = (data: HourReportFormState) => {
  if (data.method === "interval" && data.startTime && data.endTime) {
    const date = new Date(data.date)
    const start = convertHHmmToDate({ HHmm: data.startTime, date }).getTime()
    const end = convertHHmmToDate({ HHmm: data.endTime, date }).getTime()
    return start < end
  }

  if (data.method === "timer" && data.timerStartTime && data.timerEndTime) {
    const start = new Date(data.timerStartTime).getTime()
    const end = new Date(data.timerEndTime).getTime()
    return start < end
  }
  return true
}

const ensureHoursPositive = (data: HourReportFormState) => {
  if (data.method === "manual") {
    return data.hours > 0 || (data.minutes !== undefined && data.minutes >= 0)
  }
  return true
}

export const createHourReportFormSchema = (translations = defaultTranslations) => {
  return HourReportFormSchema.refine(ensureHoursPositive, {
    message: translations.hoursPositive,
    path: ["hours"],
  }).refine(ensureStartBeforeEnd, {
    message: translations.startBeforeEnd,
    path: ["startTime"],
  })
}

const defaultTranslations = {
  startBeforeEnd: "Start time must be before end time.",
  hoursPositive: "Hours must be a more than zero.",
}

export type HourReportFormState = z.infer<typeof HourReportFormSchema>
